import { FC, MouseEvent, useCallback, useRef } from 'react'
import { useSnackbar } from 'notistack'
import styled from 'styled-components'
import { getSelectionText } from 'utils'
import {
  CoverLetterDetail_CoverLetterFragment,
  CoverLetterDetail_CurrentUserFragment,
} from 'generated/graphql'
import { CoverLetterScrap } from '@app.feature/coverletter/component/CoverLetterDetail/CoverLetterDetailContent/types'
import { IS_SERVER } from 'constants/common'
import SelectionPopover, {
  SelectionPopoverActionIDs,
  SelectionPopoverProvider,
  useSelectionPopoverContext,
} from './SelectionPopover'
import {
  isScrapValid,
  getTagOffsetList,
  buildCoverLetterNodes,
  getRangeBoundingClientRect,
  renderCoverLetterContent,
} from './libs/highlightNodes'

interface CoverLetterDetailContentProps {
  currentUser?: CoverLetterDetail_CurrentUserFragment
  coverLetter?: CoverLetterDetail_CoverLetterFragment
  highlight?: string | null | undefined
}

const CoverLetterDetailContent: FC<CoverLetterDetailContentProps> = ({
  currentUser,
  coverLetter,
  highlight,
}) => {
  const rootRef = useRef<HTMLElement>(null)

  const { closePopover, openPopover } = useSelectionPopoverContext()
  const { enqueueSnackbar } = useSnackbar()

  const originalContent = coverLetter?.content ?? ''
  const viewerParagraphScraps = coverLetter?.viewerParagraphScraps || []

  const validScraps = viewerParagraphScraps.reduce<CoverLetterScrap[]>(
    (validScraps, scrap) => {
      const { id, startOffset, endOffset, paragraph } = scrap

      if (
        isScrapValid(originalContent, {
          startOffset,
          endOffset,
          paragraph,
        })
      ) {
        validScraps.push({
          id,
          startOffset,
          endOffset,
          paragraph,
        })
        return validScraps
      }

      const newStartOffset = originalContent.indexOf(paragraph)
      if (newStartOffset === -1) {
        return validScraps
      }

      const newEndOffset = newStartOffset + paragraph.length
      validScraps.push({
        id,
        startOffset: newStartOffset,
        endOffset: newEndOffset,
        paragraph,
      })
      return validScraps
    },
    [],
  )

  const searchKeywords = getTagOffsetList(highlight || '', '<b>')
  const nodes = buildCoverLetterNodes({
    content: originalContent,
    scraps: validScraps,
    keywords: searchKeywords,
  })

  const handleMouseUp = (e: MouseEvent<HTMLElement>) => {
    if (IS_SERVER) {
      return
    }

    const selection = window.getSelection()

    const target = e.target
    if (!(target instanceof HTMLElement)) {
      return
    }

    const clickedTargetID = target.id
    if (SelectionPopoverActionIDs.includes(clickedTargetID)) {
      e.stopPropagation()
      return
    }

    if (!selection?.rangeCount) {
      closePopover()
      return
    }

    const selectionString = selection.toString()
    if (selectionString.length === 0) {
      closePopover()
      return
    }

    if (selectionString.length < 5) {
      enqueueSnackbar('스크랩은 최소 5문자부터 가능합니다.')
      closePopover()
      return
    }

    const range = selection.getRangeAt(0)
    const position = getRangeBoundingClientRect(range)

    if (!position) {
      return
    }

    openPopover({
      selectionText: getSelectionText(),
      selectionRect: position,
    })
  }

  const handleMouseLeave = () => {
    closePopover()
  }

  const getMainCoordinates = useCallback(() => {
    if (IS_SERVER) {
      return undefined
    }

    return rootRef.current?.getBoundingClientRect()
  }, [])

  return (
    <StyledMain
      ref={rootRef}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      <SelectionPopover
        currentUser={currentUser}
        coverLetter={coverLetter}
        getBaseCoordinates={getMainCoordinates}
      />
      {nodes.map((node) => renderCoverLetterContent(node))}
    </StyledMain>
  )
}

const CoverLetterDetailContentContainer: FC<CoverLetterDetailContentProps> = (
  props,
) => (
  <SelectionPopoverProvider>
    <CoverLetterDetailContent {...props} />
  </SelectionPopoverProvider>
)

export default CoverLetterDetailContentContainer

const StyledMain = styled.article`
  font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.43;
  letter-spacing: 0.01071em;
  font-size: 16px;
  font-weight: normal;
  text-align: left;
  line-height: 2.13;
  letter-spacing: -0.64px;
  white-space: pre-line;
  margin-bottom: 50px;
  position: relative;
  padding-top: 25px;
`
